import { useState, useEffect } from "react"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Checkbox } from "../../../components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Button } from "../../../components/ui/button"
import { Plus, X } from "lucide-react"
import type { FieldType, MetaDataField, FieldDropdownValue } from "./Types"
import { useCategoryManagement } from "../../Services/CategoryManagement"

interface DynamicFieldFormProps {
    field: MetaDataField
    fieldTypes: FieldType[]
    onChange: (field: MetaDataField) => void
    disabled?: boolean
    isShowValues?: boolean
    categories?: { id: string; categoryName: string }[]
    errorFields?: { fieldName?: boolean; fieldDisplayName?: boolean; masterMetaDataFieldTypeId?: boolean }
}

export default function DynamicFieldForm({
    field,
    fieldTypes,
    onChange,
    disabled,
    isShowValues,
    categories: categoriesProp,
    errorFields,
}: DynamicFieldFormProps) {
    const [showAddValue, setShowAddValue] = useState(false)
    const [categories, setCategories] = useState<{ id: string; categoryName: string }[]>(categoriesProp ?? [])
    const { getCategories } = useCategoryManagement()

    useEffect(() => {
        if (!isShowValues) return
        if (categoriesProp !== undefined) {
            setCategories(categoriesProp)
            return
        }

        const fetchCategories = async () => {
            try {
                const res = await getCategories()
                if (Array.isArray(res)) {
                    setCategories(res)
                }
            } catch (error) {
                console.error("Error fetching categories:", error)
            }
        }
        fetchCategories()
    }, [isShowValues, categoriesProp])

    const [newValue, setNewValue] = useState<FieldDropdownValue>({
        code: "",
        description: "",
    })

    const selectedFieldType = fieldTypes.find((f) => f.id === field.masterMetaDataFieldTypeId)
    const isSelectType =
        selectedFieldType?.frontEndFieldType === "select" || selectedFieldType?.frontEndFieldType === "multiselect"

    const handleFieldNameChange = (value: string) => {
        onChange({ ...field, fieldName: value })
    }

    const handleDisplayNameChange = (value: string) => {
        onChange({ ...field, fieldDisplayName: value })
    }

    const handleDescriptionChange = (value: string) => {
        onChange({ ...field, fieldDescription: value })
    }

    const handleCategoryChange = (value: string) => {
        onChange({ ...field, tenantFieldCategoryId: value })
    }

    const handleFieldTypeChange = (fieldTypeId: string) => {
        onChange({
            ...field,
            masterMetaDataFieldTypeId: fieldTypeId,
            fieldData: [], // Reset dropdown values when changing type
        })
    }

    const handleRequiredChange = (checked: boolean) => {
        onChange({ ...field, isRequiredField: checked })
    }

    const handleBlockchainChange = (checked: boolean) => {
        onChange({ ...field, isSendToBlockChain: checked })
    }

    const handleAddDropdownValue = () => {
        if (newValue.code.trim()) {
            const updatedFieldData = [
                ...field.fieldData,
                {
                    ...newValue
                },
            ]
            onChange({ ...field, fieldData: updatedFieldData })
            setNewValue({ code: "", description: "" })
            setShowAddValue(false)
        }
    }

    const handleRemoveDropdownValue = (index: number) => {
        const updatedFieldData = field.fieldData.filter((_, i) => i !== index)
        onChange({ ...field, fieldData: updatedFieldData })
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs font-medium">Field Name *</Label>
                    <Input
                        placeholder="e.g., product_size"
                        value={field.fieldName}
                        onChange={(e) => handleFieldNameChange(e.target.value)}
                        disabled={disabled}
                        className={`text-sm ${errorFields?.fieldName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    />
                </div>
                <div>
                    <Label className="text-xs font-medium">Display Name *</Label>
                    <Input
                        placeholder="e.g., Product Size"
                        value={field.fieldDisplayName}
                        onChange={(e) => handleDisplayNameChange(e.target.value)}
                        disabled={disabled}
                        className={`text-sm ${errorFields?.fieldDisplayName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    />
                </div>
            </div>

            {isShowValues && (
                <div>
                    <Label className="text-xs font-medium">Description</Label>
                    <Input
                        placeholder="Field description"
                        value={field.fieldDescription}
                        onChange={(e) => handleDescriptionChange(e.target.value)}
                        disabled={disabled}
                        className="text-sm"
                    />
                </div>
            )}

            {isShowValues && (
                <div>
                    <Label className="text-xs font-medium">Product Category</Label>
                    <Select
                        value={field.tenantFieldCategoryId}
                        onValueChange={handleCategoryChange}
                        disabled={disabled}
                    >
                        <SelectTrigger className="text-sm">
                            <SelectValue placeholder="Select product category" />
                        </SelectTrigger>
                        <SelectContent>
                            {categories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                    {cat.categoryName}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {isShowValues && (
                <div>
                    <Label className="text-xs font-medium">Field Type *</Label>
                    <Select value={field.masterMetaDataFieldTypeId} onValueChange={handleFieldTypeChange} disabled={disabled}>
                        <SelectTrigger className={`text-sm ${errorFields?.masterMetaDataFieldTypeId ? "border-red-500 focus-visible:ring-red-500" : ""}`}>
                            <SelectValue placeholder="Select field type" />
                        </SelectTrigger>
                        <SelectContent>
                            {fieldTypes.map((type) => (
                                <SelectItem key={type.id} value={type.id}>
                                    {type.fieldType}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}



            <div className="flex gap-3">
                <div className="flex items-center gap-2">
                    <Checkbox
                        id={`required-${field.id}`}
                        checked={field.isRequiredField}
                        onCheckedChange={handleRequiredChange}
                        disabled={disabled}
                    />
                    <Label htmlFor={`required-${field.id}`} className="text-xs font-medium cursor-pointer">
                        Required
                    </Label>
                </div>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id={`blockchain-${field.id}`}
                        checked={field.isSendToBlockChain}
                        onCheckedChange={handleBlockchainChange}
                        disabled={disabled}
                    />
                    <Label htmlFor={`blockchain-${field.id}`} className="text-xs font-medium cursor-pointer">
                        Send to Blockchain
                    </Label>
                </div>
            </div>

            {/* Dropdown Values Section - Only show for select/multiselect */}
            {isSelectType && (
                <div className="mt-4 pt-4 border-t">
                    {isShowValues && (
                        <div className="flex items-center justify-between mb-3">
                            <Label className="text-xs font-medium">Dropdown Values</Label>
                            {!showAddValue && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setShowAddValue(true)}
                                    disabled={disabled}
                                    className="gap-1 h-7"
                                >
                                    <Plus className="w-3 h-3" />
                                    Add Value
                                </Button>
                            )}
                        </div>
                    )}

                    {showAddValue && (
                        <div className="p-3 rounded border mb-3 space-y-2">
                            <div>
                                <Label className="text-xs font-medium">Code</Label>
                                <Input
                                    placeholder="e.g., S, M, L"
                                    value={newValue.code}
                                    onChange={(e) => setNewValue({ ...newValue, code: e.target.value })}
                                    disabled={disabled}
                                    className="text-sm"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-medium">Description</Label>
                                <Input
                                    placeholder="e.g., Small"
                                    value={newValue.description}
                                    onChange={(e) => setNewValue({ ...newValue, description: e.target.value })}
                                    disabled={disabled}
                                    className="text-sm"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    onClick={handleAddDropdownValue}
                                    disabled={!newValue.code.trim() || disabled}
                                    className="h-7"
                                >
                                    Add
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setShowAddValue(false)}
                                    disabled={disabled}
                                    className="h-7"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="space-y-1">
                        {field.fieldData.map((value, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 rounded text-sm">
                                <span>
                                    <strong>{value.code}</strong> - {value.description}
                                </span>
                                {isShowValues && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleRemoveDropdownValue(idx)}
                                        disabled={disabled}
                                        className="h-5 w-5 p-0"
                                    >
                                        <X className="w-3 h-3" />
                                    </Button>
                                )}
                            </div>
                        ))}
                        {field.fieldData.length === 0 && !showAddValue && (
                            <p className="text-xs text-slate-500">No values added yet</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
